// src/pages/TrainingRefutation.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Textarea, Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingRefutation() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [theirSide, setTheirSide] = useState('Government'); // side of the AI argument
  const [ourSide, setOurSide] = useState('Opposition');     // side we’ll refute from
  const [loadingMotion, setLoadingMotion] = useState(false);

  // Generated AI argument (PAEL)
  const [aiPoint, setAiPoint] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiExample, setAiExample] = useState('');
  const [aiLink, setAiLink] = useState('');

  const [generatingArg, setGeneratingArg] = useState(false);

  // Our refutation inputs
  const [refuteImpact, setRefuteImpact] = useState('');   // prioritize impact/link
  const [refuteAnalysis, setRefuteAnalysis] = useState('');
  const [refuteExamples, setRefuteExamples] = useState('');
  const [weighing, setWeighing] = useState('');           // why our response matters more

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Helpers
  const resetAll = () => {
    setTheirSide('Government');
    setOurSide('Opposition');
    setAiPoint('');
    setAiAnalysis('');
    setAiExample('');
    setAiLink('');
    setRefuteImpact('');
    setRefuteAnalysis('');
    setRefuteExamples('');
    setWeighing('');
    setFeedback(null);
    setError('');
  };

  const newMotion = async () => {
    resetAll();
    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');
      setMotion(data.motion);

      // Randomize sides: AI side vs our side
      const aiIsGov = Math.random() < 0.5;
      setTheirSide(aiIsGov ? 'Government' : 'Opposition');
      setOurSide(aiIsGov ? 'Opposition' : 'Government');
    } catch (e) {
      console.error(e);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  const generateAIArgument = async () => {
    if (!motion) {
      setError('Generate a motion first.');
      return;
    }
    setError('');
    setFeedback(null);
    setGeneratingArg(true);
    try {
      // If your server route name differs, update it here.
      const res = await fetch(`${API_BASE}/api/generate-argument`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motion, side: theirSide }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate AI argument');

      // Expected shape:
      // { side, pael: { point, analysis, example, link }, summary? }
      const pael = data.pael || data.argument || {};
      setAiPoint(pael.point || '');
      setAiAnalysis(pael.analysis || '');
      setAiExample(pael.example || '');
      setAiLink(pael.link || '');
    } catch (e) {
      console.error(e);
      setError('Failed to generate AI argument.');
    } finally {
      setGeneratingArg(false);
    }
  };

  const submitRefutation = async () => {
    if (!motion) {
      setError('Generate a motion first.');
      return;
    }
    if (!aiPoint && !aiAnalysis && !aiExample && !aiLink) {
      setError('Generate the AI argument first.');
      return;
    }
    if (!refuteImpact.trim() || !refuteAnalysis.trim()) {
      setError('Address the impact/link and analysis at minimum.');
      return;
    }

    setError('');
    setSubmitting(true);
    setFeedback(null);
    try {
      // If your route is named differently, update here.
      const res = await fetch(`${API_BASE}/api/refutation-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motion,
          their_side: theirSide,
          our_side: ourSide,
          argument: {
            point: aiPoint,
            analysis: aiAnalysis,
            example: aiExample,
            link: aiLink,
          },
          refutation: {
            impact: refuteImpact,
            analysis: refuteAnalysis,
            examples: refuteExamples,
            weighing,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to evaluate refutation');
      setFeedback(data);
    } catch (e) {
      console.error(e);
      setError('Failed to get feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyArgument = async () => {
    const txt = `AI PAEL\n\nPoint: ${aiPoint}\n\nAnalysis: ${aiAnalysis}\n\nExample: ${aiExample}\n\nLink: ${aiLink}`;
    try { await navigator.clipboard.writeText(txt); } catch {}
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Refutation Training</h1>
          <p className="text-sm text-gray-600">
            Focus on the core first: impact and analysis. Then address examples. Explain why your response matters more.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={newMotion} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Setup */}
      <Card title="Practice Setup" subtitle="Motion and sides">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
            <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
              {motion || 'Click "New Motion" to begin.'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">AI Argument Side</label>
            <Input
              value={theirSide}
              onChange={(e) => setTheirSide(e.target.value)}
              placeholder="Government or Opposition"
            />
            <p className="mt-1 text-xs text-gray-500">This is the side the AI will generate an argument for.</p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button onClick={generateAIArgument} disabled={generatingArg || !motion}>
            {generatingArg ? <span className="inline-flex items-center gap-2"><Spinner />Building argument…</span> : 'Generate AI Argument'}
          </Button>
          <Button variant="secondary" onClick={() => setOurSide(theirSide === 'Government' ? 'Opposition' : 'Government')}>
            Set My Side Opposite
          </Button>
          <div className="ml-auto text-sm text-gray-700">
            <strong>Your Refutation Side:</strong> {ourSide || '—'}
          </div>
        </div>
      </Card>

      {/* AI Argument */}
      {(aiPoint || aiAnalysis || aiExample || aiLink) && (
        <Card title="AI Argument (PAEL)" subtitle="This is what you will refute">
          <div className="grid gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Point</label>
              <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm">{aiPoint || '—'}</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Analysis</label>
              <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap">{aiAnalysis || '—'}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Example</label>
                <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap">{aiExample || '—'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Link / Impact</label>
                <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap">{aiLink || '—'}</div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="secondary" onClick={copyArgument}>Copy PAEL</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Refutation input */}
      <Card title="Your Refutation" subtitle="Address impact/link first, then analysis, then examples. Finish with clear weighing.">
        <div className="grid gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Refute Impact / Link</label>
            <Textarea
              rows={3}
              placeholder="Challenge magnitude, probability, time horizon, or show why their link is weak or misdirected."
              value={refuteImpact}
              onChange={(e) => setRefuteImpact(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Refute Analysis</label>
            <Textarea
              rows={3}
              placeholder="Show a broken mechanism, missing steps, wrong incentives, better causal story, or contradictory evidence."
              value={refuteAnalysis}
              onChange={(e) => setRefuteAnalysis(e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Address Examples</label>
              <Textarea
                rows={2}
                placeholder="Attack representativeness, internal validity, selection, or provide a counter-example."
                value={refuteExamples}
                onChange={(e) => setRefuteExamples(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Weighing / Why This Matters More</label>
              <Textarea
                rows={2}
                placeholder="Even if their point holds a little, explain why your response dominates the round."
                value={weighing}
                onChange={(e) => setWeighing(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button onClick={submitRefutation} disabled={submitting}>
              {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Scoring…</span> : 'Submit for Feedback'}
            </Button>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </Card>

      {/* Feedback */}
      {feedback && (
        <Card title="AI Feedback" subtitle="Scores emphasize impact and analysis first, then examples">
          {/* Expected shape:
              {
                scores: {
                  impact_address, analysis_address, examples_address, prioritization, overall
                },
                comments,
                tips
              }
          */}
          <div className="space-y-3 text-sm text-gray-800">
            {feedback.scores && (
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="grid sm:grid-cols-5 gap-2">
                  {'impact_address' in feedback.scores && (
                    <p><strong>Impact/Link:</strong> {feedback.scores.impact_address}/10</p>
                  )}
                  {'analysis_address' in feedback.scores && (
                    <p><strong>Analysis:</strong> {feedback.scores.analysis_address}/10</p>
                  )}
                  {'examples_address' in feedback.scores && (
                    <p><strong>Examples:</strong> {feedback.scores.examples_address}/10</p>
                  )}
                  {'prioritization' in feedback.scores && (
                    <p><strong>Prioritization:</strong> {feedback.scores.prioritization}/10</p>
                  )}
                  {'overall' in feedback.scores && (
                    <p><strong>Overall:</strong> {feedback.scores.overall}/10</p>
                  )}
                </div>
              </div>
            )}

            {feedback.comments && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                {feedback.comments}
              </div>
            )}

            {feedback.tips && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                <strong>Tips:</strong> {feedback.tips}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
